import React from 'react';
import { Box, Flex, HStack, Text, Badge, Icon, Tooltip } from '@chakra-ui/react';
import { formatDateTime } from 'utils/date.util';
import { FiFileText, FiClock, FiDatabase } from 'react-icons/fi';

import { useColors } from './colors';

const formatSourceType = (sourceType) => {
  if (!sourceType) return 'Unknown';
  return sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
};

const InventorySourceBanner = ({ product }) => {
  const {
    sourceBannerBg,
    sourceBannerBorder,
    textColor,
    secondaryTextColor,
    accentColor,
  } = useColors();

  const uploadedAt = product.inventoryUploadedAt
    ? formatDateTime(product.inventoryUploadedAt, { format: 'DD MMM YYYY · hh:mm A' })
    : null;

  return (
    <Box
      px={4}
      py={3}
      bg={sourceBannerBg}
      borderBottomWidth="1px"
      borderColor={sourceBannerBorder}
      borderTopRadius="xl"
    >
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        gap={2}
      >
        <HStack spacing={3} align="flex-start" flex={1} minW={0}>
          <Icon as={FiFileText} color={accentColor} mt={0.5} flexShrink={0} />
          <Box minW={0}>
            <Text fontSize="xs" fontWeight="semibold" color={secondaryTextColor} textTransform="uppercase">
              Inventory file
            </Text>
            <Tooltip label={product.inventoryFileName || 'Unknown file'} hasArrow>
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={textColor}
                noOfLines={1}
              >
                {product.inventoryFileName || 'Unknown file'}
              </Text>
            </Tooltip>
          </Box>
        </HStack>

        <HStack spacing={2} flexWrap="wrap">
          {uploadedAt && (
            <Badge
              variant="outline"
              colorScheme="gray"
              px={2}
              py={1}
              borderRadius="md"
              fontSize="xs"
              fontWeight="medium"
            >
              <HStack spacing={1}>
                <Icon as={FiClock} boxSize={3} />
                <Text>{uploadedAt}</Text>
              </HStack>
            </Badge>
          )}
          <Badge
            colorScheme="purple"
            variant="subtle"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
          >
            {formatSourceType(product.inventorySourceType)}
          </Badge>
          {product.inventoryId && (
            <Tooltip label={`Inventory record ID: ${product.inventoryId}`} hasArrow>
              <Badge
                variant="outline"
                colorScheme="blue"
                px={2}
                py={1}
                borderRadius="md"
                fontSize="xs"
                fontFamily="mono"
              >
                <HStack spacing={1}>
                  <Icon as={FiDatabase} boxSize={3} />
                  <Text noOfLines={1} maxW="120px">
                    {product.inventoryId.slice(-8)}
                  </Text>
                </HStack>
              </Badge>
            </Tooltip>
          )}
        </HStack>
      </Flex>
    </Box>
  );
};

export default InventorySourceBanner;
